import { useState } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { sendInvite } from "@/lib/invites";
import { colors, radius, font } from "@/lib/theme";
import type { ClubRole } from "@/lib/types";
import ContentWidth from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

const Section = styled.View`padding: 20px;`;
const Label = styled.Text`font-size: 11px; font-weight: ${font.bold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;`;
const Input = styled.TextInput`background-color: ${colors.white}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 12px 14px; font-size: 15px; color: ${colors.ink};`;
const ErrorText = styled.Text`color: ${colors.danger}; font-size: 13px; font-weight: ${font.semibold}; margin-top: 8px;`;
const SaveBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.card}px; padding: 16px; align-items: center; margin-top: 24px;`;
const SaveBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;

export default function InviteMemberScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { alertProps, alert } = useAlert();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!club || !user || !membership || !email.trim()) return;
    setSending(true);
    setError("");
    try {
      await sendInvite(club.id, club.name, {
        email: email.trim(),
        role: "player" as ClubRole,
        teams: [],
        invitedBy: user.uid,
        invitedByName: membership.displayName,
        clubLogoUrl: club.logoUrl,
      });
      const again = await alert({
        title: "Invite sent!",
        emoji: "✅",
        message: `An invite has been sent to ${email.trim()}.`,
        buttonLabel: "Send another",
      });
      setEmail("");
    } catch (err: any) {
      setError(err.message || "Failed to send invite.");
    } finally {
      setSending(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <Section>
          <Label>Email address</Label>
          <Input
            placeholder="player@email.com"
            placeholderTextColor={colors.inkMuted}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoFocus
          />

          {error ? <ErrorText>{error}</ErrorText> : null}

          <Pressable
            onPress={handleSend}
            disabled={sending || !email.trim()}
            style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
          >
            <SaveBtn style={{ opacity: !email.trim() ? 0.5 : 1 }}>
              {sending ? <ActivityIndicator color="#fff" /> : <SaveBtnText>Send invite</SaveBtnText>}
            </SaveBtn>
          </Pressable>
        </Section>
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}
